import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { Rppk01GateGroup, Rppk01TableResult } from '../../../../core/models/report/rppk01.model';
import { ParkingGateListResponse } from '../../../../core/models/parking.model';
import { ParkingVehicleTypeResponse } from '../../../../core/models/vehicle.model';
import { MapperRppk01Service } from '../../../../core/mappers/report/mapper-rppk01.service';
import { ApiRppk01Service } from '../../../../core/services/api/report/api-rppk01.service';
import { ApiParkingCardListService } from '../../../../core/services/api/parking/api-parking-card-list.service';
import { ApiService } from '../../../../core/services/api.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { downloadReportExcel, buildReportExcelFilename } from '../../../../core/utils/report-excel-export.util';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalInvoicePdfViewerComponent } from '../../modals/finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';

@Component({
  selector: 'app-rppk01',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalInvoicePdfViewerComponent],
  templateUrl: './rppk01.component.html',
  styleUrl: './rppk01.component.css',
})
export class Rppk01Component implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiRppk01Service);
  private readonly mapper = inject(MapperRppk01Service);
  private readonly cardApi = inject(ApiParkingCardListService);
  private readonly apiService = inject(ApiService);
  private readonly toast = inject(ToastService);

  isLoading = signal(false);
  isExporting = signal(false);
  isProcessingPdf = signal(false);
  isLoadingVehicleTypes = signal(false);
  isLoadingGates = signal(false);
  showPdfModal = signal(false);
  pdfSrc: Uint8Array | null = null;
  pdfDownloadName = 'RPPK01.pdf';

  startDate = '';
  endDate = '';
  license = '';
  lastFetchedTime: Date | null = null;
  hasSearched = signal(false);

  vehicleTypes = signal<ParkingVehicleTypeResponse[]>([]);
  vehicleTypeId: number | null = null;
  searchVehicleTypeText = '';
  isVehicleTypeDropdownOpen = false;

  entryGates = signal<ParkingGateListResponse[]>([]);
  entryGateId: number | null = null;
  searchEntryGateText = '';
  isEntryGateDropdownOpen = false;

  private readonly maxRangeMonths = 2;
  result = signal<Rppk01TableResult | null>(null);

  constructor() {
    const today = this.toInputDate(new Date());
    this.startDate = today;
    this.endDate = today;

    effect(() => {
      const siteId = this.siteState.selectedSite()?.id ?? 0;
      untracked(() => {
        this.entryGateId = null;
        this.searchEntryGateText = '';
        this.isEntryGateDropdownOpen = false;
        this.clearResult();
        if (siteId > 0) {
          this.loadEntryGates(siteId);
        } else {
          this.entryGates.set([]);
        }
      });
    });
  }

  ngOnInit(): void {
    this.loadVehicleTypes();
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canSearch(): boolean {
    return this.selectedSiteId > 0 && !!this.startDate && !!this.endDate;
  }

  get groups(): Rppk01GateGroup[] {
    return this.result()?.groups ?? [];
  }

  get totals() {
    return this.result()?.totals ?? null;
  }

  get filteredVehicleTypes(): ParkingVehicleTypeResponse[] {
    const q = this.searchVehicleTypeText.trim().toLowerCase();
    const list = this.vehicleTypes();
    if (!q || q === 'ทุกประเภท') return list;
    return list.filter((v) => (v.typeName || '').toLowerCase().includes(q));
  }

  get filteredEntryGates(): ParkingGateListResponse[] {
    const q = this.searchEntryGateText.trim().toLowerCase();
    const list = this.entryGates();
    if (!q || q === 'ทุกประตู') return list;
    return list.filter((g) => (g.name || '').toLowerCase().includes(q));
  }

  get maxEndDate(): string | undefined {
    if (!this.startDate) return undefined;
    const start = this.parseInputDate(this.startDate);
    if (!start) return undefined;
    return this.toInputDate(
      new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate()),
    );
  }

  onFilterChange(): void {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
    if (this.startDate && this.endDate && this.isRangeOverMax(this.startDate, this.endDate)) {
      const maxEnd = this.maxEndDate;
      if (maxEnd) this.endDate = maxEnd;
      this.toast.warn('เรียกดูได้สูงสุด 2 เดือน — ปรับวันสิ้นสุดให้อัตโนมัติ');
    }
    this.clearResult();
  }

  selectVehicleType(opt: ParkingVehicleTypeResponse): void {
    this.vehicleTypeId = opt.id;
    this.searchVehicleTypeText = opt.typeName || '';
    this.isVehicleTypeDropdownOpen = false;
    this.clearResult();
  }

  clearVehicleType(): void {
    this.vehicleTypeId = null;
    this.searchVehicleTypeText = '';
    this.isVehicleTypeDropdownOpen = false;
    this.clearResult();
  }

  selectEntryGate(opt: ParkingGateListResponse): void {
    this.entryGateId = opt.id;
    this.searchEntryGateText = opt.name || '';
    this.isEntryGateDropdownOpen = false;
    this.clearResult();
  }

  clearEntryGate(): void {
    this.entryGateId = null;
    this.searchEntryGateText = '';
    this.isEntryGateDropdownOpen = false;
    this.clearResult();
  }

  closeDropdowns(): void {
    if (this.isVehicleTypeDropdownOpen) {
      const text = this.searchVehicleTypeText.trim().toLowerCase();
      if (!text || text === 'ทุกประเภท') {
        this.clearVehicleType();
      } else {
        const match = this.vehicleTypes().find((v) => (v.typeName || '').toLowerCase() === text);
        if (match) {
          this.vehicleTypeId = match.id;
          this.searchVehicleTypeText = match.typeName || '';
        } else if (this.vehicleTypeId) {
          this.searchVehicleTypeText =
            this.vehicleTypes().find((v) => v.id === this.vehicleTypeId)?.typeName || '';
        } else {
          this.searchVehicleTypeText = '';
        }
        this.isVehicleTypeDropdownOpen = false;
      }
    }

    if (this.isEntryGateDropdownOpen) {
      const text = this.searchEntryGateText.trim().toLowerCase();
      if (!text || text === 'ทุกประตู') {
        this.clearEntryGate();
      } else {
        const match = this.entryGates().find((g) => (g.name || '').toLowerCase() === text);
        if (match) {
          this.entryGateId = match.id;
          this.searchEntryGateText = match.name || '';
        } else if (this.entryGateId) {
          this.searchEntryGateText =
            this.entryGates().find((g) => g.id === this.entryGateId)?.name || '';
        } else {
          this.searchEntryGateText = '';
        }
        this.isEntryGateDropdownOpen = false;
      }
    }
  }

  onSearch(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }
    if (this.startDate > this.endDate) {
      this.toast.error('วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }
    this.loadTable();
  }

  onRefresh(): void {
    if (!this.hasSearched()) {
      this.onSearch();
      return;
    }
    this.loadTable();
  }

  loadTable(): void {
    if (!this.canSearch) {
      this.clearResult();
      return;
    }

    this.isLoading.set(true);
    this.api.getRppk01ForTable({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
      vehicleTypeId: this.vehicleTypeId,
      entryGateId: this.entryGateId,
      license: this.license,
    }).subscribe({
      next: (res) => {
        this.result.set(this.mapper.mapTable(res));
        this.hasSearched.set(true);
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: (err) => {
        this.result.set(null);
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลดรายงาน RPPK01 ไม่สำเร็จ');
      },
    });
  }

  async onExportExcel(): Promise<void> {
    if (!this.result() || this.groups.length === 0) {
      this.toast.error('ไม่มีข้อมูลให้ส่งออก');
      return;
    }

    this.isExporting.set(true);
    try {
      const exportRows: Record<string, string | number>[] = [];
      for (const g of this.groups) {
        for (const dg of g.dateGroups) {
          for (const r of dg.rows) {
            exportRows.push({
              'ลำดับ': r.seq,
              'ประตู': g.gateName,
              'วันที่': this.formatDateOnly(dg.entryDate),
              'บัตรจอดรถ': r.cardHolder,
              'ค่าจอดรถ': r.parkingFee === 0 ? 'Free' : r.parkingFee,
              'ประเภทรถ': r.vehicleTypeName,
              'ประเภทสติ๊กเกอร์': r.subscriptionName,
              'ทะเบียนเข้า': r.entryLicense,
              'วันเวลาเข้า': this.formatDateTime(r.entryDate),
              'วันเวลาออก': this.formatDateTime(r.exitDate),
              'เวลาจอด': r.durationText,
              'ประตูเข้า': r.entryGate,
              'ประตูออก': r.exitGate,
            });
          }
        }
        exportRows.push({
          'ลำดับ': '',
          'ประตู': `รวม ${g.gateName}`,
          'วันที่': '',
          'บัตรจอดรถ': '',
          'ค่าจอดรถ': g.sumParkingFee,
          'ประเภทรถ': '',
          'ประเภทสติ๊กเกอร์': '',
          'ทะเบียนเข้า': '',
          'วันเวลาเข้า': '',
          'วันเวลาออก': '',
          'เวลาจอด': '',
          'ประตูเข้า': '',
          'ประตูออก': '',
        });
      }
      const t = this.totals!;
      exportRows.push({
        'ลำดับ': '',
        'ประตู': `ยอดรวมทั้งสิ้น (${t.rowCount} รายการ)`,
        'วันที่': '',
        'บัตรจอดรถ': '',
        'ค่าจอดรถ': t.sumParkingFee,
        'ประเภทรถ': '',
        'ประเภทสติ๊กเกอร์': '',
        'ทะเบียนเข้า': '',
        'วันเวลาเข้า': '',
        'วันเวลาออก': '',
        'เวลาจอด': '',
        'ประตูเข้า': '',
        'ประตูออก': '',
      });

      const siteName = this.result()?.siteName || this.siteState.selectedSite()?.name || '—';
      const extraLines: string[] = [];
      if (this.vehicleTypeId) {
        const vtName = this.vehicleTypes().find((v) => v.id === this.vehicleTypeId)?.typeName;
        if (vtName) extraLines.push(`ประเภทรถ: ${vtName}`);
      }
      if (this.entryGateId) {
        const gateName = this.entryGates().find((g) => g.id === this.entryGateId)?.name;
        if (gateName) extraLines.push(`ประตูเข้า: ${gateName}`);
      }
      if (this.license.trim()) {
        extraLines.push(`ทะเบียน: ${this.license.trim()}`);
      }

      await downloadReportExcel(exportRows, {
        reportTitle: 'RPPK01 — รายงานเข้าออก',
        siteName,
        siteAddress: this.result()?.siteAddress,
        siteTaxId: this.result()?.siteTaxId,
        startDate: this.startDate,
        endDate: this.endDate,
        extraLines,
        sheetName: 'RPPK01',
        fileName: buildReportExcelFilename('RPPK01', siteName, this.startDate, this.endDate),
      });
      this.toast.success('ส่งออก Excel สำเร็จ');
    } catch {
      this.toast.error('ส่งออก Excel ไม่สำเร็จ');
    } finally {
      this.isExporting.set(false);
    }
  }

  onPrintPdf(): void {
    if (!this.canSearch) {
      this.toast.error('กรุณาเลือกลานและช่วงวันที่');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }

    this.isProcessingPdf.set(true);
    const siteName = (this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
    this.pdfDownloadName = `RPPK01_${siteName}_${this.startDate}_${this.endDate}.pdf`;

    this.api.getRppk01Pdf({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
      vehicleTypeId: this.vehicleTypeId,
      entryGateId: this.entryGateId,
      license: this.license,
    }).subscribe({
      next: (bytes) => {
        this.pdfSrc = bytes;
        this.showPdfModal.set(true);
        this.isProcessingPdf.set(false);
      },
      error: (err) => {
        console.error('[API Error] getRppk01Pdf:', err);
        this.toast.error('ไม่สามารถดึงไฟล์ PDF ได้');
        this.isProcessingPdf.set(false);
      },
    });
  }

  closePdfModal(): void {
    this.showPdfModal.set(false);
    this.pdfSrc = null;
  }

  formatFee(value: number): string {
    return value === 0 ? 'Free' : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatDateOnly(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      return String(value);
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  }

  private loadVehicleTypes(): void {
    this.isLoadingVehicleTypes.set(true);
    this.cardApi.getParkingVehicleTypes().subscribe({
      next: (list) => {
        this.vehicleTypes.set(list ?? []);
        this.isLoadingVehicleTypes.set(false);
      },
      error: () => {
        this.vehicleTypes.set([]);
        this.isLoadingVehicleTypes.set(false);
      },
    });
  }

  private loadEntryGates(siteId: number): void {
    this.isLoadingGates.set(true);
    this.apiService.getParkingGate(siteId).subscribe({
      next: (list) => {
        const all = list ?? [];
        const entryOnly = all.filter((g) => g.gateTypeId === 1);
        this.entryGates.set(entryOnly.length > 0 ? entryOnly : all);
        this.isLoadingGates.set(false);
      },
      error: () => {
        this.entryGates.set([]);
        this.isLoadingGates.set(false);
      },
    });
  }

  private clearResult(): void {
    this.result.set(null);
    this.hasSearched.set(false);
    this.lastFetchedTime = null;
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseInputDate(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private isRangeOverMax(startStr: string, endStr: string): boolean {
    const start = this.parseInputDate(startStr);
    const end = this.parseInputDate(endStr);
    if (!start || !end) return false;
    const maxEnd = new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate());
    return end > maxEnd;
  }
}
